import { FeatureFlags } from '../consts/featureFlags';
import { BootAuthResponse } from '../types';

const DEFAULT_TITLE = 'Gaming Store';

export function resolveStoreTitle(authBoot: BootAuthResponse): string {
  const seo = authBoot.storeTheme?.storeSettings?.seo ?? authBoot.storeTheme?.storeScreen?.seo;
  const general = authBoot.general || authBoot.storeTheme?.general;
  const isSeoEnabled = authBoot.featureFlags?.[FeatureFlags.DASHBOARD_STORE_SEO_SETTINGS];
  return isSeoEnabled
    ? seo?.title || DEFAULT_TITLE
    : authBoot.storeTheme?.storeSettings?.storeTabName || general?.storeTabName || DEFAULT_TITLE;
}

/**
 * Next.js 15.2+ streams async metadata — during client-side navigation the
 * <title> tag is temporarily removed while generateMetadata resolves, which
 * makes the browser briefly show the raw URL.  We work around this by
 * observing the <title> element and restoring the correct value whenever the
 * framework clears it.
 */
export function guardDocumentTitle(authBoot: BootAuthResponse): () => void {
  const storeTitle = resolveStoreTitle(authBoot);
  document.title = storeTitle;

  const observer = new MutationObserver(() => {
    if (!document.title || document.title === 'about:blank') {
      document.title = storeTitle;
    }
  });

  const titleEl = document.querySelector('title');
  if (titleEl) {
    observer.observe(titleEl, { childList: true, characterData: true, subtree: true });
  }
  observer.observe(document.head, { childList: true });

  return () => observer.disconnect();
}

export const resolveThemeColor = (authBoot: BootAuthResponse): string => {
  return authBoot?.storeTheme?.storeSettings?.themeColor || '#ffffff';
};

export const setThemeColor = (authBoot: BootAuthResponse) => {
  if (!authBoot) return;
  const themeColor = resolveThemeColor(authBoot);
  let themeColorMeta = document.querySelector('meta[name="theme-color"]');
  if (!themeColorMeta) {
    themeColorMeta = document.createElement('meta');
    themeColorMeta.setAttribute('name', 'theme-color');
    document.head.appendChild(themeColorMeta);
  }
  themeColorMeta.setAttribute('content', themeColor);
  document.documentElement.style.setProperty('--app-background-color', themeColor);
};

export const updateViewport = (bootData: BootAuthResponse) => {
  const viewportMeta = document.querySelector('meta[name="viewport"]');

  if (viewportMeta) {
    if (bootData?.featureFlags?.[FeatureFlags.STORE_DISABLE_VIEWPORT_SCALE]) {
      viewportMeta.setAttribute(
        'content',
        'width=device-width, height=device-height, initial-scale=1, maximum-scale=1, viewport-fit=cover, user-scalable=no'
      );
    } else {
      viewportMeta.setAttribute(
        'content',
        'width=device-width, height=device-height, initial-scale=1, maximum-scale=5, viewport-fit=cover, user-scalable=yes'
      );
    }
  }
};

export const setOverscrollBehavior = (authBoot: BootAuthResponse) => {
  if (!authBoot) return;
  const bouncyScrollOn =
    authBoot.storeTheme?.storeSettings?.bouncyScrollOn ||
    authBoot.storeTheme?.general?.bouncyScrollOn;

  if (bouncyScrollOn) {
    document.documentElement.style.removeProperty('overscroll-behavior');
    document.body.style.removeProperty('overscroll-behavior');
  } else {
    document.documentElement.style.overscrollBehavior = 'none';
    document.body.style.overscrollBehavior = 'none';
  }
};

export const setFavicon = (authBoot: BootAuthResponse) => {
  if (authBoot) {
    const faviconUrl =
      authBoot.storeTheme?.storeSettings?.favicon ||
      authBoot.general?.favicon ||
      authBoot.storeTheme?.general?.favicon;
    const appIcon = authBoot.storeTheme?.storeScreen?.addToHomeScreen?.active
      ? authBoot.storeTheme.storeScreen?.addToHomeScreen?.iconImage
      : faviconUrl;

    if (faviconUrl) {
      // Update main favicon
      let fav = document.getElementById('favicon');
      if (!fav) {
        fav = document.createElement('link');
        fav.id = 'favicon';
        fav.setAttribute('rel', 'shortcut icon');
        document.head.appendChild(fav);
      }
      if (fav) {
        fav.setAttribute('href', faviconUrl);
      }

      // Update PNG favicons
      let favicon32 = document.querySelector('link[rel="icon"][sizes="32x32"]');
      if (!favicon32) {
        favicon32 = document.createElement('link');
        favicon32.setAttribute('rel', 'icon');
        favicon32.setAttribute('type', 'image/png');
        favicon32.setAttribute('sizes', '32x32');
        document.head.appendChild(favicon32);
      }
      if (favicon32) {
        favicon32.setAttribute('href', faviconUrl);
      }

      let favicon16 = document.querySelector('link[rel="icon"][sizes="16x16"]');
      if (!favicon16) {
        favicon16 = document.createElement('link');
        favicon16.setAttribute('rel', 'icon');
        favicon16.setAttribute('type', 'image/png');
        favicon16.setAttribute('sizes', '16x16');
        document.head.appendChild(favicon16);
      }
      if (favicon16) {
        favicon16.setAttribute('href', faviconUrl);
      }
    }

    // Update Apple Touch Icon
    let appleTouchIcon = document.querySelector('link[rel="apple-touch-icon"]');
    if (!appleTouchIcon) {
      appleTouchIcon = document.createElement('link');
      appleTouchIcon.setAttribute('rel', 'apple-touch-icon');
      document.head.appendChild(appleTouchIcon);
    }
    if (appleTouchIcon && appIcon) {
      appleTouchIcon.setAttribute('href', appIcon);
    }
  }
};

export const createAppManifest = (authBoot: BootAuthResponse) => {
  const appIcon = authBoot.storeTheme?.storeScreen?.addToHomeScreen?.active
    ? authBoot.storeTheme.storeScreen?.addToHomeScreen?.iconImage
    : authBoot.storeTheme?.storeSettings?.favicon ||
      authBoot.general?.favicon ||
      authBoot.storeTheme?.general?.favicon;
  const storeName = authBoot.storeTheme?.storeScreen?.addToHomeScreen?.active
    ? authBoot.storeTheme.storeScreen?.addToHomeScreen?.shortcutName
    : authBoot.storeTheme?.storeSettings?.storeTabName ||
      authBoot.general?.storeTabName ||
      authBoot.storeTheme?.general?.storeTabName ||
      'Gaming App';
  if (!appIcon) console.error('Add to home screen icon is missing in the publisher metadata');
  const iconAppSrc = appIcon;
  const manifest = {
    name: storeName,
    short_name: storeName,
    ...(iconAppSrc
      ? {
          icons: [
            {
              src: iconAppSrc,
              type: 'image/png',
              sizes: '192x192',
            },
            {
              src: iconAppSrc,
              type: 'image/png',
              sizes: '512x512',
            },
          ],
          apple_touch_icon: iconAppSrc,
        }
      : {}),
    display: 'browser',
    theme_color: resolveThemeColor(authBoot),
    background_color: '#ffffff',
    start_url: `${window.location.origin}${window.location.pathname.replace('/login', '/shop')}?mode=standalone`,
    apple_mobile_web_app_capable: 'yes',
    apple_mobile_web_app_status_bar_style: 'black-translucent',
    apple_mobile_web_app_title:
      authBoot.storeTheme?.storeSettings?.storeTabName ||
      authBoot.general?.storeTabName ||
      authBoot.storeTheme?.general?.storeTabName ||
      'Gaming App',
  };

  const blob = new Blob([JSON.stringify(manifest)], {
    type: 'application/json',
  });
  const manifestURL = URL.createObjectURL(blob);

  const link = document.createElement('link');
  link.rel = 'manifest';
  link.href = manifestURL;
  document.head.appendChild(link);
};
